import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_LEAD_MS    = 2 * 60 * 1000;   // warn 2 min before auto-logout

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [permissionDefaults, setPermissionDefaults] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_LEAD_MS / 1000));
  const timeoutRef  = useRef(null);   // hard logout timer
  const warnTimerRef = useRef(null);  // pre-logout warning timer
  const countdownRef = useRef(null);  // 1s countdown interval while warning is shown
  const userRef = useRef(null);
  const permissionsFetchedForUserRef = useRef(null);
  const loading = profileLoading || permissionsLoading;

  const signOutUser = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null); setProfile(null); setPermissionDefaults(null);
    userRef.current = null;
    permissionsFetchedForUserRef.current = null;
  }, []);

  // Reset the inactivity clock. Schedules a warning 2 min before logout so an
  // active-but-not-clicking user (e.g. a nurse mid-assessment) can stay signed
  // in with one click — instead of being dropped silently. Preserves the HIPAA
  // auto-logoff control: doing nothing still logs out at 30 min.
  const resetTimer = useCallback(() => {
    if (timeoutRef.current)  clearTimeout(timeoutRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setShowTimeoutWarning(false);
    if (!userRef.current) return;

    warnTimerRef.current = setTimeout(() => {
      setSecondsLeft(Math.floor(WARNING_LEAD_MS / 1000));
      setShowTimeoutWarning(true);
      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => (s > 1 ? s - 1 : 0));
      }, 1000);
    }, SESSION_TIMEOUT_MS - WARNING_LEAD_MS);

    timeoutRef.current = setTimeout(() => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      setShowTimeoutWarning(false);
      signOutUser();
    }, SESSION_TIMEOUT_MS);
  }, [signOutUser]);

  const fetchProfile = useCallback(async (userId) => {
    // Retry up to 3 times to handle race conditions
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name, role, active, modules')
          .eq('id', userId)
          .single();

        if (!error && data) {
          setProfile(data);
          setProfileLoading(false);
          return;
        }

        // Wait 500ms before retrying
        if (attempt < 3) await new Promise(r => setTimeout(r, 500));
      } catch (err) {
        console.error('Profile fetch error:', err);
      }
    }

    // If all retries fail, set a minimal profile but keep trying
    console.warn('Could not load profile after 3 attempts, using fallback');
    setProfile({ id: userId, full_name: 'Loading...', role: 'staff' });
    setProfileLoading(false);
  }, []);

  // Live role->module defaults (mirrors the `permissions` table an admin edits
  // in core/Permissions.js). Fetched once per signed-in user, not on every
  // token refresh. Fails safe: leaves permissionDefaults null so useModules()
  // falls through to its static DEFAULT_PERMISSIONS fallback.
  const fetchPermissionDefaults = useCallback(async (userId) => {
    if (permissionsFetchedForUserRef.current === userId) {
      setPermissionsLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('role, module, can_view, can_edit, can_delete, can_export');

      if (!error && data) {
        const map = {};
        data.forEach(row => {
          if (!map[row.role]) map[row.role] = {};
          map[row.role][row.module] = {
            can_view:   !!row.can_view,
            can_edit:   !!row.can_edit,
            can_delete: !!row.can_delete,
            can_export: !!row.can_export,
          };
        });
        setPermissionDefaults(map);
      }
      permissionsFetchedForUserRef.current = userId;
    } catch (err) {
      console.error('Permission defaults fetch error:', err);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u); userRef.current = u;
      if (u) { fetchProfile(u.id); fetchPermissionDefaults(u.id); resetTimer(); }
      else { setProfileLoading(false); setPermissionsLoading(false); }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u); userRef.current = u;
      if (u) { fetchProfile(u.id); fetchPermissionDefaults(u.id); resetTimer(); }
      else {
        setProfile(null); setProfileLoading(false);
        setPermissionDefaults(null); setPermissionsLoading(false);
        permissionsFetchedForUserRef.current = null;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
    });

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => { if (userRef.current) resetTimer(); };
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      subscription.unsubscribe();
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (timeoutRef.current)  clearTimeout(timeoutRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [fetchProfile, fetchPermissionDefaults, resetTimer]);

  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');

  return (
    <AuthContext.Provider value={{ user, profile, loading, signOutUser, permissionDefaults }}>
      {children}
      {showTimeoutWarning && (
        <div
          role="alertdialog"
          aria-label="Session timeout warning"
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,28,48,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000, padding: 20 }}
        >
          <div style={{ background: 'var(--card-bg)', borderRadius: 14, maxWidth: 420, width: '100%', padding: '28px 26px', textAlign: 'center', fontFamily: 'var(--font)', boxShadow: '0 16px 48px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 34, marginBottom: 8 }}>⏳</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>Still there?</div>
            <div style={{ fontSize: 13.5, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 6 }}>
              You'll be signed out due to inactivity in
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: secondsLeft <= 30 ? 'var(--danger)' : 'var(--accent)', marginBottom: 16, fontVariantNumeric: 'tabular-nums' }}>
              {mins}:{secs}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 20 }}>
              Any unsaved work is backed up, but click below to stay signed in and keep saving.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => resetTimer()}
                style={{ flex: 1, padding: '11px 0', background: 'var(--success)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Stay signed in
              </button>
              <button
                onClick={() => { setShowTimeoutWarning(false); signOutUser(); }}
                style={{ padding: '11px 18px', background: 'transparent', color: 'var(--text2)', border: '0.5px solid var(--border2)', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
